<template>
    <section
        class="overflow-hidden rounded-[12.8px] bg-gradient-to-br from-[#4f46e5] via-[#7c3aed] to-[#db2777] text-white shadow-lg shadow-violet-200/60"
    >
        <button
            type="button"
            class="flex w-full items-center gap-4 px-[21px] py-[18px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            :aria-expanded="isExpanded"
            aria-controls="ai-dashboard-preview-body"
            @click="toggleExpanded"
        >
            <span
                class="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12.8px] bg-white/15 ring-1 ring-white/30"
            >
                <SparklesIcon class="h-6 w-6" />
            </span>

            <span class="min-w-0 flex-1">
                <span class="flex flex-wrap items-center gap-2">
                    <span class="font-medium">
                        {{ t("vue.dashboard_overview_ai_title") }}
                    </span>
                    <span
                        class="rounded-full bg-white/20 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
                    >
                        {{ t("vue.dashboard_overview_ai_badge") }}
                    </span>
                </span>
                <span
                    class="mt-1 block truncate text-[13px] leading-[18px] text-white/80"
                >
                    {{ t("vue.dashboard_overview_ai_subtitle") }}
                </span>
            </span>

            <span
                class="flex shrink-0 items-center gap-2 text-[13px] font-medium text-white/90"
            >
                {{
                    isExpanded
                        ? t("vue.dashboard_overview_ai_collapse")
                        : t("vue.dashboard_overview_ai_expand")
                }}
                <ChevronDownIcon
                    class="h-5 w-5 transition-transform duration-300"
                    :class="isExpanded ? 'rotate-180' : ''"
                />
            </span>
        </button>

        <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="max-h-0 opacity-0"
            enter-to-class="max-h-[640px] opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="max-h-[640px] opacity-100"
            leave-to-class="max-h-0 opacity-0"
        >
            <div
                v-show="isExpanded"
                id="ai-dashboard-preview-body"
                class="overflow-hidden"
            >
                <div class="border-t border-white/15 px-[21px] pb-[21px] pt-4">
                    <p
                        class="max-w-3xl text-[13px] leading-[20px] text-white/90"
                    >
                        {{ t("vue.dashboard_overview_ai_description") }}
                    </p>

                    <div class="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div
                            v-for="tile in tiles"
                            :key="tile.key"
                            class="rounded-[12.8px] bg-white/10 p-4 ring-1 ring-white/20"
                        >
                            <div
                                class="flex items-center justify-between gap-2"
                            >
                                <p class="text-[13px] font-medium">
                                    {{ tile.label }}
                                </p>
                                <span
                                    class="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/80"
                                >
                                    {{
                                        t(
                                            "vue.dashboard_overview_ai_coming_soon",
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="mt-3 space-y-2">
                                <span
                                    v-for="width in tile.barWidths"
                                    :key="width"
                                    class="block h-2 animate-pulse rounded-full bg-white/25"
                                    :style="{ width }"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="mt-5 flex flex-wrap items-center gap-3">
                        <Link
                            v-if="canOpenChat"
                            :href="route('ai-chat.index')"
                            class="inline-flex items-center gap-2 rounded-md bg-white px-4 py-2 text-[13px] font-medium text-[#4f46e5] shadow-sm hover:bg-white/90"
                        >
                            <ChatBubbleLeftRightIcon class="h-4 w-4" />
                            {{ t("vue.dashboard_overview_ai_open_chat") }}
                        </Link>
                        <Link
                            v-if="canOpenAdminDashboard"
                            :href="route('ai-chat.dashboard.index')"
                            class="inline-flex items-center gap-2 rounded-md border border-white/40 px-4 py-2 text-[13px] font-medium text-white hover:bg-white/10"
                        >
                            {{
                                t(
                                    "vue.dashboard_overview_ai_open_admin_dashboard",
                                )
                            }}
                        </Link>
                        <p
                            v-if="!canOpenChat"
                            class="text-[13px] text-white/80"
                        >
                            {{ t("vue.dashboard_overview_ai_unavailable") }}
                        </p>
                    </div>
                </div>
            </div>
        </Transition>
    </section>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { Link, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useLocalStorage } from "@vueuse/core";
import {
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    SparklesIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";

interface PreviewTile {
    key: string;
    label: string;
    barWidths: string[];
}

const EXPANDED_STORAGE_KEY = "dmarceye.dashboard.aiPreviewExpanded";

const { t } = useTranslations();
const page = usePage<IRhizomPageProps>();

const isExpanded = useLocalStorage<boolean>(EXPANDED_STORAGE_KEY, false);

const toggleExpanded = (): void => {
    isExpanded.value = !isExpanded.value;
};

const canOpenChat = computed<boolean>(
    () => page.props.aiChat?.can_access === true,
);

const canOpenAdminDashboard = computed<boolean>(
    () =>
        canOpenChat.value &&
        page.props.isAdmin === true &&
        page.props.inAppAiChatDashboardActive === true,
);

const tiles = computed<PreviewTile[]>(() => [
    {
        key: "highlights",
        label: t("vue.dashboard_overview_ai_tile_highlights"),
        barWidths: ["88%", "64%", "76%"],
    },
    {
        key: "risks",
        label: t("vue.dashboard_overview_ai_tile_risks"),
        barWidths: ["72%", "90%", "55%"],
    },
    {
        key: "recommendations",
        label: t("vue.dashboard_overview_ai_tile_recommendations"),
        barWidths: ["80%", "60%", "70%"],
    },
]);
</script>
